import axios from 'axios';

// Determinar a URL base automaticamente
// Regra: se tiver definido no .env, usar .env; se não tiver, usar fallback de desenvolvimento
export const getBaseURL = () => {
  // Se houver variável de ambiente, usar ela (sem barras duplicadas no final)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/+$/, '');
  }

  // Fallback apenas para desenvolvimento local sem .env configurado
  return 'http://localhost:3001/api';
};

// Configuração base da API
// Nota: getBaseURL() será chamado na primeira requisição
const api = axios.create({
  timeout: process.env.REACT_APP_API_TIMEOUT || 15000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // Importante para CORS em desenvolvimento
});

// Definir baseURL dinamicamente a cada requisição
api.defaults.baseURL = getBaseURL();

// Interceptor de REQUISIÇÃO - Garante uso do localhost em desenvolvimento
api.interceptors.request.use(
  (config) => {
    // Forçar re-checagem da baseURL em cada requisição
    const currentBaseURL = getBaseURL();
    
    // Se a baseURL configurada for diferente da atual, atualizar
    if (config.baseURL !== currentBaseURL) {
      config.baseURL = currentBaseURL;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor de RESPOSTA simplificado
api.interceptors.response.use(
  response => response,
  async (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// Funções específicas para planos
const checkoutSessionHeaders = (token) =>
  token ? { 'X-Checkout-Session': token } : {};

export const planosAPI = {
  getPlanos: () => api.get('/planos'),
  getValores: () => api.get('/planos/valores'),
  getValorFrete: () => api.get('/planos/frete'),
  createCheckoutSession: () => api.post('/planos/checkout-session'),
  validaCpf: (cpf, checkoutSessionToken) =>
    api.post('/planos/valida-cpf', { cpf }, {
      headers: checkoutSessionHeaders(checkoutSessionToken),
    }),
  submitContratacao: (payload, checkoutSessionToken) =>
    api.post('/planos/contratar', payload, {
      headers: {
        'Content-Type': 'application/json',
        ...checkoutSessionHeaders(checkoutSessionToken),
      },
    }),
};


// Helpers para formatação de dados
export const formatters = {
  formatDocumento: (documento) => {
    if (!documento) return '';
    
    // Remove todos os caracteres não numéricos
    const digits = String(documento).replace(/\D/g, '');

    if (digits.length > 11) {
      // Formato CNPJ (XX.XXX.XXX/XXXX-XX)
      return digits.substring(0, 14)
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
        .replace(/(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d{1,2})/, '$1.$2.$3/$4-$5');
    }
    // Formato CPF (XXX.XXX.XXX-XX)
    return digits.substring(0, 11)
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d{1,2})/, '$1.$2.$3-$4');
  },
  
  formatPhone: (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{2})(\d{4,5})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  },
  
  formatCurrency: (value) => {
    if (!value || isNaN(parseFloat(value))) return 'R$ 0,00';
    
    try {
      const numValue = parseFloat(value);
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numValue);
    } catch (error) {
      console.warn('Erro ao formatar valor:', value, error);
      return 'R$ 0,00';
    }
  },
  
  formatDate: (date) => {
    if (!date) return '';
    
    try {
      // Se já estiver em DD/MM/AAAA, retornar como está
      const ddmmyyyy = /^\d{2}\/\d{2}\/\d{4}$/;
      if (ddmmyyyy.test(date)) {
        return date;
      }
      
      // Tentar criar um objeto Date
      const parsedDate = new Date(date);
      
      // Verificar se a data é válida
      if (isNaN(parsedDate.getTime())) {
        return '';
      }
      
      return new Intl.DateTimeFormat('pt-BR').format(parsedDate);
    } catch (error) {
      console.warn('Erro ao formatar data:', date, error);
      return '';
    }
  },
};

export default api;
